import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function DevToolsPage(): Node {
  return html`
    <div>
      <h1>DevTools</h1>
      <p><code>enableDevtools</code> activates browser console integration for inspecting signals, effects, and component trees during development.</p>

      <h2>Enable in Development</h2>
      ${CodeBlock(`import { enableDevtools } from 'onefold/devtools';

if (import.meta.env?.MODE === 'development') {
  enableDevtools();
}`)}

      <h2>Features</h2>
      <ul>
        <li><strong>Signal Inspector</strong> — View all active signals and their current values.</li>
        <li><strong>Effect Tracker</strong> — See which effects depend on which signals.</li>
        <li><strong>Update Log</strong> — Console log of every signal change and resulting DOM update.</li>
        <li><strong>Component Tree</strong> — Visualize the component hierarchy.</li>
        <li><strong>Performance</strong> — Track update timing and identify slow renders.</li>
      </ul>

      <h2>Disable</h2>
      ${CodeBlock(`import { disableDevtools } from 'onefold/devtools';

// Turn off devtools (e.g., before production build check)
disableDevtools();`)}

      ${Callout('DevTools add runtime overhead. Never enable them in production. Use conditional checks like import.meta.env.MODE.')}

      <h2>Console API</h2>
      <p>When devtools are enabled, a global <code>__ONEFOLD__</code> object is available in the browser console:</p>
      ${CodeBlock(`// In browser console:
__ONEFOLD__.signals       // List all active signals
__ONEFOLD__.effects       // List all active effects
__ONEFOLD__.components    // Component tree
__ONEFOLD__.inspect(signal) // Detailed info about a signal`)}

      <h2>API</h2>
      <table>
        <tr><th>Function</th><th>Description</th></tr>
        <tr><td><code>enableDevtools()</code></td><td>Activate devtools integration.</td></tr>
        <tr><td><code>disableDevtools()</code></td><td>Deactivate devtools integration.</td></tr>
      </table>

      <h2>Try It</h2>
      <p>Enable devtools and see render performance stats update as you interact:</p>

      ${Playground(`import { createSignal, html, mount } from 'onefold';
import { enableDevtools } from 'onefold/devtools';

function App() {
  const devtools = enableDevtools();
  const count = createSignal(0);
  const tick = createSignal(0);

  function refresh() { tick.set(n => n + 1); }

  function increment() {
    count.set(n => n + 1);
    refresh();
  }

  function burst() {
    for (let i = 0; i < 20; i++) count.set(n => n + 1);
    refresh();
  }

  return html\`
    <div>
      <h3>DevTools — Performance Monitor</h3>
      <p style="font-size:13px;color:#666;margin-bottom:12px">
        enableDevtools() hooks into the effect system to track every render.
      </p>

      <div style="display:flex;gap:8px;margin-bottom:16px;flex-wrap:wrap">
        <button onclick=\${increment}>Increment (\${() => count()})</button>
        <button onclick=\${burst}>Burst +20</button>
        <button onclick=\${() => { devtools.clear(); count.set(0); refresh(); }} style="font-size:12px">Clear Stats</button>
      </div>

      <div style="background:#0f172a;color:#a5f3fc;padding:12px;border-radius:8px;font-family:monospace;font-size:12px">
        \${() => {
          tick();
          const s = devtools.stats();
          return html\`
            <div>Total renders: <strong>\${s.totalRenders}</strong></div>
            <div>Avg duration: <strong>\${s.avgDuration.toFixed(3)}ms</strong></div>
            <div>Slowest: <strong>\${s.slowestRender ? s.slowestRender.duration.toFixed(3) + 'ms (' + s.slowestRender.label + ')' : 'N/A'}</strong></div>
            <div>Errors: <strong>\${s.totalErrors}</strong></div>
            <div style="margin-top:6px;color:#64748b">Render entries: \${devtools.renders.length} | Active: \${devtools.active ? 'yes' : 'no'}</div>
          \`;
        }}
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'enableDevtools — Render Profiling')}
    </div>
  `;
}
